"use client";

import { usePathname, useRouter } from "next/navigation";
import {
	createContext,
	useContext,
	useEffect,
	useState,
	useCallback,
} from "react";
import { UserType } from "@/types/auth";
import { getOperatorsMe } from "@/services/operator";
import { getAgentsMe } from "@/services/agent";
import { getAdminsMe } from "@/services/admin";
import { getOperatorSupportMe } from "@/services/support";
import {
	DEFAULT_CURRENCY,
	isThb,
	operatorCurrency,
} from "@/constants/currency";

interface AuthContextType {
	fetchUserData: () => Promise<void>;
	token: string | null;
	userId: string | null;
	username: string | null;
	maxRtpPercent: number | null;
	role: UserType | null;
	privateKey: string | null;
	defaultPresetId: string;
	login: (role: UserType, token: string) => void;
	logout: () => void;
	canCreateDemoOperator: boolean;
	isDemoAccount: boolean;
	demoExpirationTime: string | null;
	parentId: string | null;
	winningQuotaRatePerUnit: number | null;
	canBuyQuota: boolean;
	allowRewardFlow: boolean;
	// The signed-in Operator may open games in any currency their provider serves, not only its
	// own: every demo account, and the operators the server lists (MULTI_CURRENCY_OPERATOR_IDS).
	playsAnyCurrency: boolean;
	// The signed-in Operator's currency (its quota, bets and payout caps are in it); THB for the
	// other roles.
	currency: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(
	undefined,
);

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};

const AuthProvider = ({ children }: any) => {
	const [token, setToken] = useState<string | null>(() => {
		if (typeof window !== "undefined") {
			return sessionStorage.getItem("token") || localStorage.getItem("token");
		}
		return null;
	});
	const [userId, setUserId] = useState<string | null>(null);
	const [username, setUsername] = useState<string | null>(null);
	const [maxRtpPercent, setMaxRtpPercent] = useState<number | null>(null);
	const [privateKey, setPrivateKey] = useState<string | null>(null);
	const [role, setRole] = useState<UserType | null>(() => {
		if (typeof window !== "undefined") {
			return (sessionStorage.getItem("role") ||
				localStorage.getItem("role")) as UserType | null;
		}
		return null;
	});
	const [canCreateDemoOperator, setCanCreateDemoOperator] = useState(false);
	const [isDemoAccount, setIsDemoAccount] = useState(false);
	const [defaultPresetId, setDefaultPresetId] = useState("");
	const [demoExpirationTime, setDemoExpirationTime] = useState<string | null>(
		null,
	);
	const [parentId, setParentId] = useState<string | null>(null);
	const [winningQuotaRatePerUnit, setWinningQuotaRatePerUnit] = useState<
		number | null
	>(null);
	const [canBuyQuota, setCanBuyQuota] = useState(false);
	const [allowRewardFlow, setAllowRewardFlow] = useState(false);
	const [playsAnyCurrency, setPlaysAnyCurrency] = useState(false);
	const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
	const [isFetching, setIsFetching] = useState(false);
	const router = useRouter();
	const pathname = usePathname();

	const login = (role: UserType, token: string) => {
		setToken(token);
		setRole(role);
		localStorage.setItem("token", token);
		localStorage.setItem("role", role);
	};

	const logout = useCallback(() => {
		setToken(null);
		setRole(null);
		setUserId(null);
		setUsername(null);
		setPrivateKey(null);
		setIsFetching(false);
		localStorage.removeItem("token");
		localStorage.removeItem("role");
		sessionStorage.removeItem("token");
		sessionStorage.removeItem("role");
		router.push(`/login`);
	}, [router]);

	const fetchUserData = useCallback(async () => {
		setIsFetching(true);
		try {
			const storedRole = (sessionStorage.getItem("role") ||
				localStorage.getItem("role")) as UserType | null;
			if (!storedRole) {
				logout();
				return;
			}
			// Define a map of functions
			const getUserInfoFunctions: { [K in UserType]?: () => Promise<any> } = {
				[UserType.Admin]: getAdminsMe,
				[UserType.Agent]: getAgentsMe,
				[UserType.Operator]: getOperatorsMe,
				[UserType.OperatorSupport]: getOperatorSupportMe,
				[UserType.OperatorSupportDev]: getOperatorSupportMe,
			};

			// Ensure the function exists for the given role
			const getUserInfo = getUserInfoFunctions[storedRole];
			if (!getUserInfo) {
				throw new Error(
					`No user info function available for role: ${storedRole}`,
				);
			}

			const fetchedUserInfo = await getUserInfo();

			// NOTE: expired demo trials are intentionally NOT logged out here.
			// An expired demo operator must still be able to sign in and reach the
			// console to top up credit. Game-play itself stays gated server-side
			// (playGame / initOperatorSession -> "Demo account expired"), so the
			// expiry is enforced where it matters without locking them out of the
			// back office. The layout still shows the expiry countdown banner.

			setUserId(fetchedUserInfo.id);
			setUsername(fetchedUserInfo.username);
			setMaxRtpPercent(fetchedUserInfo.maxRtpPercent || null);
			setRole(storedRole);
			// getMe no longer returns the private key (fetched on demand via the
			// api-docs reveal endpoint); keep the context field null.
			setPrivateKey(fetchedUserInfo.privateKey ?? null);
			setCanCreateDemoOperator(fetchedUserInfo.canCreateDemoOperator || false);
			setIsDemoAccount(fetchedUserInfo.isDemoAccount || false);
			setDemoExpirationTime(fetchedUserInfo.demoAccountExpireAt);
			setDefaultPresetId(fetchedUserInfo.defaultPresetId);
			setParentId(fetchedUserInfo.parentAgentId || fetchedUserInfo.agentId);
			setWinningQuotaRatePerUnit(
				fetchedUserInfo.winningQuotaRatePerUnit || null,
			);
			const userCurrency =
				storedRole === UserType.Operator
					? operatorCurrency(fetchedUserInfo)
					: DEFAULT_CURRENCY;
			setCurrency(userCurrency);
			// Quota is bought in THB; an Operator in another currency gets it from its Agent only.
			setCanBuyQuota(
				(fetchedUserInfo.canBuyQuota || false) && isThb(userCurrency),
			);
			setAllowRewardFlow(fetchedUserInfo.allowRewardFlow || false);
			setPlaysAnyCurrency(
				storedRole === UserType.Operator &&
					(fetchedUserInfo.playsAnyCurrency || false),
			);
		} catch (error: any) {
			logout();
		} finally {
			setIsFetching(false);
		}
	}, [logout]);

	useEffect(() => {
		if (isFetching) {
			return;
		}

		if (pathname === "/login" || pathname === "/login-as") {
			return;
		}

		if (role !== null && userId !== null) {
			return;
		}

		const storedToken =
			sessionStorage.getItem("token") || localStorage.getItem("token");
		if (!storedToken) {
			router.push("/login");
			return;
		}

		fetchUserData();
	}, [pathname, role, userId, isFetching, fetchUserData, router]);

	// Redirect authenticated users away from login page
	useEffect(() => {
		if (role !== null && pathname === "/login") {
			if (role === UserType.Admin) {
				router.push("/admin/operator-list");
			} else if (role === UserType.Agent) {
				router.push("/agent/home");
			} else if (role === UserType.Operator) {
				router.push("/operator/home");
			} else if (role === UserType.OperatorSupport) {
				router.push("/support/spin-order-summary");
			} else if (role === UserType.OperatorSupportDev) {
				router.push("/support/home");
			}
		}
	}, [role, pathname, router]);

	return (
		<AuthContext.Provider
			value={{
				fetchUserData,
				token,
				userId,
				username,
				maxRtpPercent,
				role,
				privateKey,
				defaultPresetId,
				login,
				logout,
				canCreateDemoOperator,
				isDemoAccount,
				demoExpirationTime,
				parentId,
				winningQuotaRatePerUnit,
				canBuyQuota,
				allowRewardFlow,
				playsAnyCurrency,
				currency,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export default AuthProvider;
